import type { FC } from 'react';
import { useState, Fragment, useContext } from 'react';
import type { Theme } from '@material-ui/core/styles';
import {
  makeStyles, createStyles, lighten,
} from '@material-ui/core/styles';
import {
  Grid, Drawer, Typography,
} from '@material-ui/core';

import type { LinkData } from './NavigationTypes';
import NavigationLinks from './NavigationLinks';
import { colors } from '../../theme';
import { EmployeeContext } from '../global/EmployeeProvider';

const useStyles = makeStyles((theme: Theme) => createStyles({
  list: {
    width: '30rem',
    margin: theme.spacing(3),
  },
  backgroundOverride: {
    background: colors.toolBarColor,
  },
  linkHeader: {
    color: lighten(colors.toolBarColor, 0.25),
    letterSpacing: '2px',
    marginBottom: '1rem',
    textTransform: 'uppercase',
  },
}));

interface NavProps {
  drawerOpen: boolean,
  drawerOpenToggle: () => void,
}

export const linkDataConst: LinkData[] = [
  {
    sectionTitle: 'Navigation',
    itemSize: 12,
    linkData: [
      { displayText: 'Home', link: '/' },
      { displayText: 'Item Photo View', link: '/item-photo-view' },
    ],
  },
  {
    sectionTitle: 'Administration',
    itemSize: 12,
    requiredRole: 'Executive',
    linkData: [
      { displayText: 'Configure Work Views', link: '/configureWorkViews', requiredRole: 'Executive' },
      { displayText: 'Users', link: '/view-users', requiredRole: 'Executive' },
      { displayText: 'Stores', link: '/stores', requiredRole: 'Executive' },
      { displayText: 'Jobs', link: '/jobs' },
      { displayText: 'Companies', link: '/companies', requiredRole: 'Executive' },
      { displayText: 'Repair Cost', link: '/repair-cost', requiredRole: 'Executive' },
      { displayText: 'Repair Locations', link: '/repair-locations', requiredRole: 'Executive' },
      { displayText: 'Repair Reject Reasons', link: '/repair-reject-reasons', requiredRole: 'Executive' },
      { displayText: 'Metal Bins', link: '/metal-bins', requiredRole: 'Executive' },
      { displayText: 'Locations', link: '/locations', requiredRole: 'Executive' },
      { displayText: 'Scanners', link: '/screenless-scanners', requiredRole: 'Executive' },
      { displayText: 'Skill Level Types', link: '/skill-level-types', requiredRole: 'Executive' },
    ],
  },
  {
    sectionTitle: 'Manager',
    itemSize: 12,
    requiredRole: 'Manager',
    linkData: [
      { displayText: 'Work Reassignment', link: '/work-reassignment', requiredRole: 'Manager' },
      { displayText: 'Work View', link: '/work-view', requiredRole: 'Manager' },
      { displayText: 'Repairs at Wayfinder', link: '/repair-lookup/repairs-at-wayfinder', requiredRole: 'Manager' },
      { displayText: 'Repair Lookup', link: '/repair-lookup', requiredRole: 'Manager' },
      { displayText: 'Repair Detail', link: '/repair-detail', requiredRole: 'Manager' },
      { displayText: 'Box Detail', link: '/box-detail', requiredRole: 'Manager' },
    ],
  },
  {
    sectionTitle: 'Receiving',
    itemSize: 12,
    linkData: [
      { displayText: 'Receive Boxes From Carrier', link: '/receive-boxes-from-carrier', requiredJobFunction: ['Intake'] },
      { displayText: 'Open Box', link: '/open-box', requiredJobFunction: ['Opener'] },
      { displayText: 'Mark Repair', link: '/mark-repair', requiredJobFunction: ['Marker'] },
      { displayText: 'Estimate Repair', link: '/estimate-repair', requiredJobFunction: ['Estimator'] },
      { displayText: 'Photograph Repair', link: '/photograph-repair', requiredJobFunction: ['Photographer'] },
      {
        displayText: 'Mass Receive', link: '/mass-receive', requiredJobFunction: ['Mass Receive'],
      },
      { displayText: 'Courier', link: '/courier', requiredJobFunction: ['Courier'] },
    ],
  },
  {
    sectionTitle: 'Production - Misc',
    requiredDepartment: 'Misc',
    itemSize: 12,
    linkData: [
      { displayText: 'Metal Collection', link: '/metal-collection', requiredJobFunction: ['Metal Collection'] },
      { displayText: 'Packer', link: '/packer', requiredJobFunction: ['Packer'] },
      { displayText: 'Jeweler', link: '/jeweler', requiredJobFunction: ['Misc'] },
      { displayText: 'Engraver', link: '/engraver', requiredJobFunction: ['Engraver'] },
      // { displayText: 'Watches', link: '/watches', requiredJobFunction: ['Watches'] },
    ],
  },
  {
    sectionTitle: 'Production - Sizing',
    requiredDepartment: 'Sizing',
    itemSize: 12,
    linkData: [
      { displayText: 'Metal Collection', link: '/metal-collection', requiredJobFunction: ['Metal Collection'] },
      { displayText: 'Packer', link: '/packer', requiredJobFunction: ['Packer'] },
      { displayText: 'Sizing', link: '/sizing', requiredJobFunction: ['Size Up', 'Size Down'] },
    ],
  },
  {
    sectionTitle: 'Policy Configurator',
    requiredDepartment: 'Administration',
    requiredRole: 'Executive',
    itemSize: 12,
    linkData: [
      {
        displayText: 'Pricing Rules',
        link: '/pricing-rules',
        requiredDepartment: 'Administration',
        requiredRole: 'Executive',
        nestedLinks: [
          {
            displayText: 'Create Pricing Rule', link: '/create-pricing-rule', requiredDepartment: 'Administration', requiredRole: 'Executive',
          },
          {
            displayText: 'View Pricing Rules', link: '/pricing-rules', requiredDepartment: 'Administration', requiredRole: 'Executive',
          },
        ],
      },
      {
        displayText: 'Routing Rules',
        link: '/routing-rules',
        requiredDepartment: 'Administration',
        requiredRole: 'Executive',
        nestedLinks: [
          {
            displayText: 'Create Routing Rule', link: '/create-routing-rule', requiredDepartment: 'Administration', requiredRole: 'Executive',
          },
          {
            displayText: 'View Routing Rules', link: '/routing-rules', requiredDepartment: 'Administration', requiredRole: 'Executive',
          },
        ],
      },
      {
        displayText: 'Marking Rules',
        link: '/marking-rules',
        requiredDepartment: 'Administration',
        requiredRole: 'Executive',
        nestedLinks: [
          {
            displayText: 'Create Marking Rule', link: '/create-marking-rule', requiredDepartment: 'Administration', requiredRole: 'Executive',
          },
          {
            displayText: 'View Marking Rules', link: '/marking-rules', requiredDepartment: 'Administration', requiredRole: 'Executive',
          },
        ],
      },
      {
        displayText: 'Photography Dynamic Rules',
        link: '/photographyDynamic-rules',
        requiredDepartment: 'Administration',
        requiredRole: 'Executive',
        nestedLinks: [
          {
            displayText: 'Create Photography Dynamic Rule', link: '/create-photographyDynamic-rule', requiredDepartment: 'Administration', requiredRole: 'Executive',
          },
          {
            displayText: 'View Photography Dynamic Rule', link: '/photographyDynamic-rules', requiredDepartment: 'Administration', requiredRole: 'Executive',
          },
        ],
      },
      {
        displayText: 'Marking Dynamic Behavior Rules',
        link: '/markingDynamicBehavior-rules',
        requiredDepartment: 'Administration',
        requiredRole: 'Executive',
        nestedLinks: [
          {
            displayText: 'Create Marking Dynamic Behavior Rule', link: '/create-markingDynamicBehavior-rule', requiredDepartment: 'Administration', requiredRole: 'Executive',
          },
          {
            displayText: 'View Marking Dynamic Behavior Rules', link: '/markingDynamicBehavior-rules', requiredDepartment: 'Administration', requiredRole: 'Executive',
          },
        ],
      },
    ],
  },
];

const NavigationDrawer: FC<NavProps> = ({ drawerOpen, drawerOpenToggle }) => {
  const classes = useStyles();
  const [navLinkState] = useState<LinkData[]>(linkDataConst);

  const EmployeeInformation = useContext(EmployeeContext);

  const LinkMap = (
    navLinkState.map((linkItem: LinkData) => {
      if (
        (linkItem.requiredRole || linkItem.requiredDepartment || linkItem.linkData.some((ld) => ld.requiredRole))
        && (EmployeeInformation.employeeInfo?.filter((e) => (e?.department === linkItem.requiredDepartment || e?.role === 'Executive'))).length === 0
        && (EmployeeInformation.employeeInfo?.filter((e) => (e?.role === linkItem.requiredRole || e?.role === 'Executive'))).length === 0
      ) {
        return <></>;
      } return (
        <Fragment key={linkItem.sectionTitle}>
          <Typography gutterBottom className={classes.linkHeader}>
            {linkItem.sectionTitle}
          </Typography>
          <Grid container spacing={1}>
            <NavigationLinks
              linkProp={linkItem.linkData}
              itemSize={linkItem.itemSize}
              linkOnClickProp={() => drawerOpenToggle()}
            />
          </Grid>
        </Fragment>
      );
    }));

  return (
    <>
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => drawerOpenToggle()}
        classes={{ paper: classes.backgroundOverride }}
      >
        <div
          className={classes.list}
          role="presentation"
        >
          {LinkMap}
        </div>
      </Drawer>
    </>

  );
};
export default NavigationDrawer;
